import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import api, { formatApiError } from "@/lib/api";
import { REGIONES, ESPECIALIDADES_ABG, ESPECIALIDADES_MED, CORTES_APELACIONES, CORTES_MAP, comunasForRegion } from "@/lib/constants";
import { formatRut, cleanRut, isValidRut } from "@/lib/rut";
import { BadgeCheck, Clock, FileText, Upload, Download, ExternalLink, Crown, ArrowUpRight, HeartHandshake, Info, Building2, Video, Coins, X } from "lucide-react";
import AvatarUploader from "@/components/AvatarUploader";
import BankingInfoSection from "@/components/BankingInfoSection";
import ProfileCompletionCard from "@/components/ProfileCompletionCard";
import HelpHint from "@/components/HelpHint";
import { playPreferredSound, getPreferredSound, isSoundEnabled, setSoundEnabled } from "@/lib/sounds";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

export default function Profile() {
  const { user, updateProfile, refresh } = useAuth();
  const { config } = useConfig();
  const isRequester = user?.role === "requester";
  const isPro = user?.role === "lawyer" || user?.role === "mediator";

  // Extrae los 8 dígitos "nacionales" a partir del teléfono almacenado (para el prefijo fijo +56 9 del solicitante)
  const initialPhoneLocal = (() => {
    const raw = String(user?.phone || "").replace(/\D/g, "");
    // Elimina 569 al inicio si viene con prefijo internacional
    if (raw.startsWith("569")) return raw.slice(3, 11);
    if (raw.startsWith("56") && raw.length >= 10) return raw.slice(2, 11);
    return raw.slice(-8);
  })();

  const [form, setForm] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    phone_local: initialPhoneLocal, // solo dígitos, para el input del solicitante
    bio: isPro ? (user?.bio || "") : "",
    region: user?.region || "",
    comuna: user?.comuna || "",
    registry_number: isPro ? (user?.registry_number || "") : "",
    rut: user?.rut || "",
    domicilio_profesional: user?.domicilio_profesional || "",
    specialties: isPro ? (user?.specialties || []) : [],
  });
  const [msg, setMsg] = useState({ ok: false, error: "" });
  const [rutFieldError, setRutFieldError] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const ESPS = user?.role === "mediator" ? ESPECIALIDADES_MED : ESPECIALIDADES_ABG;
  const toggleSpec = (s) =>
    setForm((f) => ({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] }));

  const submit = async (e) => {
    e.preventDefault();
    setMsg({ ok: false, error: "" });
    try {
      // RUT: obligatorio Y válido para todos los roles
      if (!form.rut || !form.rut.trim()) {
        setMsg({ ok: false, error: "El RUT es obligatorio." });
        return;
      }
      if (!isValidRut(form.rut)) {
        setMsg({ ok: false, error: "RUT inválido. Verifica el número y el dígito verificador." });
        return;
      }
      let payload;
      if (isRequester) {
        // Solicitante: sólo enviamos campos relevantes. El teléfono se compone del prefijo +56 9 + 8 dígitos.
        const digits = String(form.phone_local || "").replace(/\D/g, "").slice(0, 8);
        if (digits.length !== 8) {
          setMsg({ ok: false, error: "El teléfono debe tener 8 dígitos después del prefijo +56 9." });
          return;
        }
        payload = {
          name: form.name,
          rut: cleanRut(form.rut),
          domicilio_profesional: form.domicilio_profesional, // se guarda en el mismo campo backend, la etiqueta es "Dirección Particular"
          region: form.region,
          comuna: form.comuna,
          phone: `+56 9 ${digits}`,
        };
      } else {
        payload = { ...form, rut: form.rut ? cleanRut(form.rut) : "" };
        delete payload.phone_local;
      }
      await updateProfile(payload);
      setMsg({ ok: true, error: "" });
    }
    catch (err) { setMsg({ ok: false, error: formatApiError(err.response?.data?.detail) || err.message }); }
  };

  if (!user) return null;

  return (
    <div className="container-app py-12 max-w-4xl" data-testid="profile-page">
      <div className="label-eyebrow">Mi cuenta</div>
      <h1 className="serif text-5xl mt-3">{user.name}</h1>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {user.verified ? <span className="pill badge-verified"><BadgeCheck size={12}/>Verificado</span>
          : <span className="pill badge-pending"><Clock size={12}/>En verificación</span>}
        <span className="pill capitalize">{
          user.role === "lawyer" ? "Abogado/a" :
          user.role === "mediator" ? "Mediador/a" :
          user.role === "admin" ? "Administrador/a" :
          user.role === "requester" ? "Persona solicitante" :
          "Usuario"
        }</span>
        <span className="pill">{user.email}</span>
        {user.has_certificate && <span className="pill badge-cyan"><FileText size={12}/>Certificado en archivo</span>}
        {user.plan !== "founder" && user.role !== "admin" && (
          <Link to="/planes" className="pill inline-flex items-center gap-1 border-amber-400/40 text-amber-300 hover:bg-amber-400/10" data-testid="profile-ver-planes">
            <Crown size={12}/> Ver planes <ArrowUpRight size={10}/>
          </Link>
        )}
      </div>

      {user.role === "lawyer" && <CertificateCard user={user}/>}

      {(user.role === "lawyer" || user.role === "mediator" || user.role === "requester") && (
        <ProfileCompletionCard user={user}/>
      )}

      {(user.role === "lawyer" || user.role === "mediator") && (
        <div className="mt-8 panel p-6" data-testid="avatar-uploader">
          <AvatarUploader />
        </div>
      )}

      <form onSubmit={submit} className="mt-8 panel p-8 grid sm:grid-cols-2 gap-5" data-testid="profile-form">
        <Field label="Nombre completo *"><input required className="field" value={form.name} onChange={(e)=>set("name", e.target.value)} data-testid="prof-name"/></Field>

        {isRequester ? (
          <Field label="Teléfono *">
            <div className="flex">
              <span
                className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-white/10 bg-white/5 text-sm text-muted-foreground select-none"
                data-testid="prof-phone-prefix"
              >
                +56 9
              </span>
              <input
                required
                inputMode="numeric"
                pattern="[0-9]{8}"
                maxLength={8}
                className="field rounded-l-none flex-1"
                value={form.phone_local}
                onChange={(e) => set("phone_local", e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="12345678"
                data-testid="prof-phone"
              />
            </div>
          </Field>
        ) : (
          <Field label={<>Teléfono * <HelpHint>Formato internacional: +56 9 1234 5678. Se muestra al cliente sólo después del pago inicial.</HelpHint></>}>
            <input required className="field" value={form.phone} onChange={(e)=>set("phone", e.target.value)} data-testid="prof-phone" placeholder="+56 9 …"/>
          </Field>
        )}

        <Field label="Región *">
          <select required className="field" value={form.region} onChange={(e)=>{
            const r = e.target.value;
            // Al cambiar la región, si la comuna actual no pertenece a la nueva región, se limpia.
            const nuevas = comunasForRegion(r);
            setForm((f) => ({ ...f, region: r, comuna: nuevas.includes(f.comuna) ? f.comuna : "" }));
          }} data-testid="prof-region">
            <option value="">Selecciona…</option>
            {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </Field>
        <Field label="Comuna *">
          <select required className="field" value={form.comuna} onChange={(e)=>set("comuna", e.target.value)} data-testid="prof-comuna" disabled={!form.region}>
            <option value="">{form.region ? "Selecciona…" : "Primero elige región"}</option>
            {comunasForRegion(form.region).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </Field>
        <Field label={<>RUT * <HelpHint>Formato: 12345678-9 (con dígito verificador). Sin puntos ni espacios.</HelpHint></>} hint={rutFieldError}>
          <input
            required
            className={`field ${rutFieldError ? "border-red-400/60 focus:border-red-400" : ""}`}
            value={form.rut}
            onChange={(e)=>{
              const formatted = formatRut(e.target.value);
              set("rut", formatted);
              if (!formatted || isValidRut(formatted)) {
                setRutFieldError("");
                if (msg && !msg.ok && (msg.error || "").startsWith("RUT")) setMsg(null);
              }
            }}
            onBlur={(e)=>{
              const v = e.target.value;
              if (v && !isValidRut(v)) setRutFieldError("RUT inválido. Verifica el número y el dígito verificador.");
              else setRutFieldError("");
            }}
            data-testid="prof-rut"
            placeholder="12345678-9"
          />
        </Field>

        {isRequester && (
          <Field label="Correo electrónico">
            <input
              type="email"
              className="field opacity-70 cursor-not-allowed"
              value={user.email}
              readOnly
              disabled
              data-testid="prof-email"
            />
          </Field>
        )}

        {isPro && (
          user?.role === "lawyer" ? (
            <Field label={<>Número de registro del Colegio de Abogados (opcional) <HelpHint>Campo opcional. Si estás inscrito en el Colegio de Abogados, ingresa aquí tu número de registro. Puedes dejarlo en blanco.</HelpHint></>}>
              <input className="field" value={form.registry_number} onChange={(e)=>set("registry_number", e.target.value)} data-testid="prof-registry" placeholder="Ej: 12345"/>
            </Field>
          ) : (
            <Field label={<>N° Registro Min. Justicia (opcional para abogados) <HelpHint>Abogados: N° de Corte Suprema (ej: ABG-12345) — no obligatorio. Mediadores: N° de Ministerio de Justicia (ej: MED-12345) — obligatorio.</HelpHint></>}>
              <input className="field" value={form.registry_number} onChange={(e)=>set("registry_number", e.target.value)} data-testid="prof-registry" placeholder="Ej: ABG-12345 / MED-12345"/>
            </Field>
          )
        )}

        {isRequester ? (
          <Field label="Dirección Particular *" className="sm:col-span-2">
            <input
              required
              className="field"
              value={form.domicilio_profesional}
              onChange={(e)=>set("domicilio_profesional", e.target.value)}
              data-testid="prof-address"
              placeholder="Calle 123, Depto 45, Comuna"
            />
          </Field>
        ) : (
          <Field label={<>Domicilio profesional * <HelpHint>Dirección de tu oficina. Solo se comparte con el cliente después del pago inicial (Pago Protegido).</HelpHint></>} className="sm:col-span-2">
            <input required className="field" value={form.domicilio_profesional} onChange={(e)=>set("domicilio_profesional", e.target.value)} data-testid="prof-address" placeholder="Calle 123, Of. 405, Santiago"/>
          </Field>
        )}

        {isPro && (
          <Field label={<>Bio * <HelpHint>1-2 párrafos: años de experiencia, universidad, especializaciones. Mín. 100 caracteres para inspirar confianza.</HelpHint></>} className="sm:col-span-2">
            <textarea required className="field" rows={3} value={form.bio} onChange={(e)=>set("bio", e.target.value)} data-testid="prof-bio" placeholder="Cuéntanos sobre tu experiencia profesional"/>
          </Field>
        )}

        {isPro && (
          <div className="sm:col-span-2">
            <div className="label-eyebrow mb-2">Especialidades</div>
            <div className="flex flex-wrap gap-2">
              {ESPS.map((s) => (
                <button type="button" key={s} onClick={()=>toggleSpec(s)} className={`pill ${form.specialties.includes(s) ? "bg-foreground text-background" : ""}`} data-testid={`prof-spec-${s}`}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {msg.ok && <div className="sm:col-span-2 text-sm text-[hsl(var(--accent))]" data-testid="prof-saved">Cambios guardados.</div>}
        {msg.error && <div className="sm:col-span-2 text-sm text-[hsl(var(--destructive))]">{msg.error}</div>}
        <div className="sm:col-span-2">
          <button className="btn-primary" data-testid="prof-submit">Guardar cambios</button>
        </div>
      </form>

      {user.role === "mediator" && (config.new_family_mediation_enabled || config.new_mediation_enabled) && (
        <MediatorFamiliarSection user={user} refresh={refresh}/>
      )}

      {(user.role === "lawyer" || user.role === "mediator") && (
        <BankingInfoSection user={user} onUpdated={() => refresh()} />
      )}

      <NotificationSoundCard />
    </div>
  );
}

function NotificationSoundCard() {
  const [enabled, setEnabled] = useState(isSoundEnabled());
  const currentKey = getPreferredSound();

  const KEY_LABEL = {
    voz: 'ColegaLink · Firma + Voz "Colega Link"',
    vozOff: "ColegaLink · Firma sonora (sin voz)",
  };

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    setSoundEnabled(next);
  };

  return (
    <div className="panel p-6 mt-6" data-testid="profile-sound-card">
      <div className="label-eyebrow">Sonido de notificaciones</div>
      <div className="serif text-2xl mt-1">Tu firma sonora</div>
      <p className="text-sm text-[hsl(var(--muted-foreground))] mt-2 max-w-2xl">
        Personaliza el sonido que se reproduce cuando recibes una notificación
        (nuevas mediaciones, asignaciones, mensajes, pagos, cambios de estado).
        Tu selección se guarda en este navegador/dispositivo.
      </p>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/40 px-4 py-2 text-sm">
          <span className="text-[hsl(var(--muted-foreground))]">Actual:</span>{" "}
          <span className="font-medium">{KEY_LABEL[currentKey] || currentKey}</span>
        </div>
        <button
          type="button"
          onClick={() => playPreferredSound()}
          className="btn-outline"
          data-testid="profile-sound-preview"
        >
          Escuchar
        </button>
        <Link
          to="/app/preview-sonidos"
          className="btn-primary"
          data-testid="profile-sound-change"
        >
          Cambiar sonido
        </Link>
        <button
          type="button"
          onClick={toggle}
          className={`px-4 py-2 rounded-xl border text-sm font-medium transition ${
            enabled
              ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/40 hover:bg-emerald-500/20"
              : "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]"
          }`}
          data-testid="profile-sound-toggle"
        >
          {enabled ? "🔔 Activado" : "🔕 Silenciado"}
        </button>
      </div>
    </div>
  );
}

function Field({ label, children, className = "", hint = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-red-300" data-testid="field-hint">{hint}</div>}
    </label>
  );
}

function CertificateCard({ user }) {
  const { refresh } = useAuth();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setErr("Archivo demasiado grande (máx 5MB)"); return; }
    setBusy(true); setErr("");
    try {
      const data_b64 = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result).split(",")[1]);
        fr.onerror = reject;
        fr.readAsDataURL(file);
      });
      await api.post("/auth/certificate", {
        filename: file.name, mime: file.type || "application/pdf", data_b64,
      });
      await refresh();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const download = async () => {
    try {
      const { data } = await api.get("/auth/certificate");
      const f = data.file;
      const bytes = Uint8Array.from(atob(f.data_b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: f.mime || "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = f.filename; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="mt-8 panel-glow p-6" data-testid="certificate-card">
      <div className="label-eyebrow text-[hsl(var(--primary))]">Verificación de credencial</div>
      <h3 className="serif text-2xl mt-1">Certificado de título</h3>
      <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
        Sube el certificado emitido por la Corte Suprema (con firma electrónica) para acreditar tu calidad de abogado.
        El equipo lo validará en <a href="https://www.pjud.cl" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] link-underline">pjud.cl</a>.
      </p>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        {user.has_certificate ? (
          <>
            <span className="pill badge-verified"><FileText size={12}/>{user.certificate_filename || "certificado.pdf"}</span>
            <button onClick={download} className="btn-ghost text-sm py-2" data-testid="prof-cert-download">
              <Download size={14} className="mr-1.5"/> Descargar
            </button>
          </>
        ) : (
          <span className="pill badge-pending"><Clock size={12}/>Pendiente de subir</span>
        )}
        <a href="https://www.pjud.cl" target="_blank" rel="noreferrer" className="btn-ghost text-sm py-2">
          <ExternalLink size={14} className="mr-1.5"/> Ir a PJUD
        </a>
      </div>

      <label className="drop-zone block mt-5" data-testid="prof-cert-zone">
        <Upload size={20} className="mx-auto text-[hsl(var(--primary))]"/>
        <div className="serif text-lg mt-2">{busy ? "Subiendo…" : (user.has_certificate ? "Reemplazar certificado" : "Subir certificado")}</div>
        <p className="text-xs text-muted-foreground mt-1">Solo PDF · máx 5MB</p>
        <input type="file" accept=".pdf" className="hidden" onChange={(e)=>upload(e.target.files?.[0])} disabled={busy} data-testid="prof-cert-input"/>
      </label>
      {err && <div className="mt-3 text-sm text-[hsl(var(--destructive))]">{err}</div>}
    </div>
  );
}

function MediatorFamiliarSection({ user, refresh }) {
  const { config } = useConfig();
  const [officeForm, setOfficeForm] = useState({
    mediator_registry_number: user?.mediator_registry_number || user?.registry_number || "",
    office_address: user?.office_address || "",
    attends_presencial: !!user?.attends_presencial,
    attends_remote: !!user?.attends_remote,
    horario_disponibilidad: user?.horario_disponibilidad || user?.availability_note || "",
    descripcion_profesional: user?.descripcion_profesional || user?.bio || "",
    cortes_apelaciones: Array.isArray(user?.cortes_apelaciones) ? user.cortes_apelaciones : [],
    comunas_atendidas: Array.isArray(user?.comunas_atendidas) ? user.comunas_atendidas : [],
    juzgados_atendidos: Array.isArray(user?.juzgados_atendidos) ? user.juzgados_atendidos : [],
  });
  const [juzgadoInput, setJuzgadoInput] = useState("");
  // Semántica del formulario:
  //  · `precio_mediador` = honorario base neto que recibe el mediador si la mediación es frustrada
  //  · `precio_acuerdo_adicional` = monto EXTRA que cobra el mediador si se alcanza acuerdo (adicional al base)
  // Backend guarda `valor_base_frustrada_clp` y `valor_acuerdo_total_clp = precio_mediador + precio_acuerdo_adicional`.
  const initialBase = user?.mediator_tariff?.valor_base_frustrada_clp || 0;
  const initialAcuerdoTotal = user?.mediator_tariff?.valor_acuerdo_total_clp || 0;
  const initialAdicional = Math.max(0, initialAcuerdoTotal - initialBase);
  const [tariffForm, setTariffForm] = useState({
    precio_mediador: initialBase,
    precio_acuerdo_adicional: initialAdicional,
  });
  const [savingOffice, setSavingOffice] = useState(false);
  const [savingTariff, setSavingTariff] = useState(false);
  const [officeMsg, setOfficeMsg] = useState({ ok: false, error: "" });
  const [tariffMsg, setTariffMsg] = useState({ ok: false, error: "" });
  const dirComplete = user?.mediator_directory_complete;

  // Cálculo transparente (mismo motor que backend) — lee la Configuración de Negocio
  // dinámica: si el admin cambia el cargo fijo o la comisión, esto se actualiza solo.
  const INITIAL_FEE = Number(config?.business_config?.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
  const AGREEMENT_RATE = Number(config?.business_config?.porcentaje_comision_acuerdo ?? config?.colega_link_agreement_rate ?? 0.08);
  const CAP = Number(config?.business_config?.tope_total_solicitante_clp ?? config?.family_mediation_total_cap_clp ?? 130602);
  const { base, adicional, acuerdo, comision, pagoInicial, pagoFinal, totalAcuerdo, totalFrustrada, maxAdicional, overCap } = useMemo(() => {
    const base = Math.max(0, parseInt(tariffForm.precio_mediador || 0, 10));
    const adicional = Math.max(0, parseInt(tariffForm.precio_acuerdo_adicional || 0, 10));
    const acuerdo = base + adicional; // valor_acuerdo_total_clp que se envía al backend
    const comision = Math.ceil(adicional * AGREEMENT_RATE);
    const pagoInicial = base + INITIAL_FEE; // precio_total_frustrada
    const pagoFinal = adicional + comision;
    const totalAcuerdo = pagoInicial + pagoFinal;
    const totalFrustrada = pagoInicial;
    // Tope de precio_acuerdo_adicional: floor((CAP - precio_mediador - INITIAL_FEE) / (1 + AGREEMENT_RATE))
    const maxAdicional = Math.max(0, Math.floor((CAP - base - INITIAL_FEE) / (1 + AGREEMENT_RATE)));
    return { base, adicional, acuerdo, comision, pagoInicial, pagoFinal, totalAcuerdo, totalFrustrada, maxAdicional, overCap: totalAcuerdo > CAP };
  }, [tariffForm, INITIAL_FEE, AGREEMENT_RATE, CAP]);

  // Comunas disponibles según cortes seleccionadas
  const comunasDisponibles = useMemo(() => {
    const set = new Set();
    officeForm.cortes_apelaciones.forEach((code) => {
      (CORTES_MAP[code]?.comunas || []).forEach((c) => set.add(c));
    });
    return Array.from(set).sort();
  }, [officeForm.cortes_apelaciones]);

  const toggleCorte = (code) => {
    setOfficeForm((f) => {
      const has = f.cortes_apelaciones.includes(code);
      const nuevas = has ? f.cortes_apelaciones.filter((c) => c !== code) : [...f.cortes_apelaciones, code];
      // Depurar comunas que ya no correspondan a las cortes seleccionadas
      const nuevasComunas = new Set();
      nuevas.forEach((cc) => (CORTES_MAP[cc]?.comunas || []).forEach((x) => nuevasComunas.add(x)));
      const comunasFiltradas = f.comunas_atendidas.filter((c) => nuevasComunas.has(c));
      return { ...f, cortes_apelaciones: nuevas, comunas_atendidas: comunasFiltradas };
    });
  };
  const toggleComuna = (c) => setOfficeForm((f) => ({ ...f, comunas_atendidas: f.comunas_atendidas.includes(c) ? f.comunas_atendidas.filter((x) => x !== c) : [...f.comunas_atendidas, c] }));
  const addJuzgado = () => {
    const v = juzgadoInput.trim();
    if (!v || officeForm.juzgados_atendidos.includes(v)) return;
    setOfficeForm((f) => ({ ...f, juzgados_atendidos: [...f.juzgados_atendidos, v] }));
    setJuzgadoInput("");
  };
  const removeJuzgado = (v) => setOfficeForm((f) => ({ ...f, juzgados_atendidos: f.juzgados_atendidos.filter((x) => x !== v) }));

  const saveOffice = async (e) => {
    e.preventDefault(); setSavingOffice(true); setOfficeMsg({ ok: false, error: "" });
    try {
      await api.put("/auth/profile", officeForm);
      if (refresh) await refresh();
      setOfficeMsg({ ok: true, error: "" });
    } catch (err) { setOfficeMsg({ ok: false, error: formatApiError(err.response?.data?.detail) || err.message }); }
    finally { setSavingOffice(false); }
  };
  const saveTariff = async (e) => {
    e.preventDefault();
    // El único límite es el tope legal total ($130.602 al solicitante).
    // Se permite cualquier honorario del mediador siempre que el TOTAL final no lo exceda.
    if (overCap) {
      setTariffMsg({ ok: false, error: `El total final que paga la persona solicitante ($${totalAcuerdo.toLocaleString("es-CL")}) supera el tope legal de arancel de mediadores familiares ($${CAP.toLocaleString("es-CL")}). Ajusta el honorario base y/o el adicional por acuerdo.` });
      return;
    }
    setSavingTariff(true); setTariffMsg({ ok: false, error: "" });
    try {
      // El backend guarda `valor_base_frustrada_clp` y `valor_acuerdo_total_clp` (total = base + adicional)
      await api.put("/auth/mediator-tariff", {
        valor_base_frustrada_clp: base,
        valor_acuerdo_total_clp: acuerdo,  // base + adicional
      });
      if (refresh) await refresh();
      setTariffMsg({ ok: true, error: "" });
    } catch (err) { setTariffMsg({ ok: false, error: formatApiError(err.response?.data?.detail) || err.message }); }
    finally { setSavingTariff(false); }
  };

  return (
    <div className="mt-10" data-testid="mediator-familiar-section">
      <div className="label-eyebrow text-cyan-300">Perfil de Mediación Familiar</div>
      <h2 className="serif text-3xl mt-2 flex items-center gap-2"><HeartHandshake size={22} className="text-cyan-300"/> Datos para el directorio</h2>
      <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
        Aparecerás en el directorio público sólo cuando completes: <strong>Cortes de Apelaciones habilitadas</strong>, <strong>al menos una comuna atendida</strong>, <strong>al menos una modalidad</strong> (dirección de oficina si atiendes presencial), estar <strong>verificado</strong>, y tarifario con <strong>honorario base</strong> y <strong>honorario total con acuerdo</strong>.
      </p>
      <div className={`mt-3 pill inline-flex text-xs ${dirComplete ? "badge-verified" : "badge-pending"}`} data-testid="mediator-directory-status">
        {dirComplete ? "✓ Publicado en el directorio" : "Perfil incompleto · aún no apareces en /mediacion-familiar"}
      </div>

      {/* Perfil profesional del mediador */}
      <form onSubmit={saveOffice} className="mt-6 panel p-6 space-y-6" data-testid="mediator-office-form">
        <div>
          <label className="text-xs label-eyebrow">Cortes de Apelaciones donde estás habilitado/a *</label>
          <div className="mt-2 flex flex-wrap gap-2" data-testid="cortes-selector">
            {CORTES_APELACIONES.map((c) => {
              const active = officeForm.cortes_apelaciones.includes(c.code);
              return (
                <button type="button" key={c.code} onClick={() => toggleCorte(c.code)}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${active ? "bg-cyan-400 text-cyan-950 border-cyan-400" : "bg-white/5 border-white/10 text-muted-foreground hover:text-foreground"}`}
                  data-testid={`corte-${c.code}`}>
                  {c.name}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Selección múltiple. Las comunas disponibles se filtran según las cortes elegidas.</p>
        </div>

        <div>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <label className="text-xs label-eyebrow">Comunas atendidas * {comunasDisponibles.length > 0 && <span className="text-muted-foreground">({officeForm.comunas_atendidas.length} de {comunasDisponibles.length})</span>}</label>
            {comunasDisponibles.length > 0 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOfficeForm((f) => ({ ...f, comunas_atendidas: [...comunasDisponibles] }))}
                  className="text-[11px] px-2.5 py-1 rounded-md border border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/10"
                  data-testid="comunas-select-all"
                >
                  Seleccionar todas
                </button>
                {officeForm.comunas_atendidas.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setOfficeForm((f) => ({ ...f, comunas_atendidas: [] }))}
                    className="text-[11px] px-2.5 py-1 rounded-md border border-white/15 text-muted-foreground hover:text-foreground"
                    data-testid="comunas-clear-all"
                  >
                    Limpiar
                  </button>
                )}
              </div>
            )}
          </div>
          {comunasDisponibles.length === 0 ? (
            <p className="text-xs text-muted-foreground mt-2 italic">Selecciona primero una o más Cortes de Apelaciones.</p>
          ) : (
            <div className="mt-2 flex flex-wrap gap-1.5 max-h-64 overflow-y-auto p-2 rounded-md bg-white/5 border border-white/10" data-testid="comunas-selector">
              {comunasDisponibles.map((c) => {
                const active = officeForm.comunas_atendidas.includes(c);
                return (
                  <button type="button" key={c} onClick={() => toggleComuna(c)}
                    className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${active ? "bg-emerald-400 text-emerald-950 border-emerald-400" : "bg-white/5 border-white/10 text-muted-foreground hover:text-foreground"}`}
                    data-testid={`comuna-${c}`}>
                    {c}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <label className="text-xs label-eyebrow">Juzgados o territorios específicos (opcional)</label>
          <div className="mt-2 flex gap-2">
            <input className="field flex-1" value={juzgadoInput} onChange={(e) => setJuzgadoInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addJuzgado(); } }} placeholder="Ej: Juzgado de Familia de Pudahuel" data-testid="juzgado-input"/>
            <button type="button" className="btn-ghost text-xs" onClick={addJuzgado} data-testid="juzgado-add">Agregar</button>
          </div>
          {officeForm.juzgados_atendidos.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {officeForm.juzgados_atendidos.map((j) => (
                <span key={j} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] bg-white/5 border border-white/10">
                  {j}
                  <button type="button" onClick={() => removeJuzgado(j)} className="hover:text-[hsl(var(--destructive))]" data-testid={`juzgado-remove-${j}`}><X size={10}/></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-4">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={officeForm.attends_presencial} onChange={(e)=>setOfficeForm({...officeForm, attends_presencial: e.target.checked})} data-testid="med-presencial"/>
            <Building2 size={14}/> Atiendo presencial
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={officeForm.attends_remote} onChange={(e)=>setOfficeForm({...officeForm, attends_remote: e.target.checked})} data-testid="med-remote"/>
            <Video size={14}/> Atiendo telemática
          </label>
        </div>

        <Field label="Horarios y disponibilidad semanal *">
          <textarea required className="field" rows={2} value={officeForm.horario_disponibilidad} onChange={(e)=>setOfficeForm({...officeForm, horario_disponibilidad: e.target.value})} placeholder="Ej: Lunes a viernes 10:00–18:00 · Sábados 10:00–14:00 (presencial y telemática)" data-testid="med-availability"/>
        </Field>
        <Field label="Descripción profesional breve">
          <textarea className="field" rows={3} value={officeForm.descripcion_profesional} onChange={(e)=>setOfficeForm({...officeForm, descripcion_profesional: e.target.value})} placeholder="Tu experiencia, enfoque profesional y forma de trabajo. Sin datos de contacto." data-testid="med-description"/>
        </Field>

        {officeMsg.ok && <div className="text-sm text-[hsl(var(--accent))]" data-testid="med-office-saved">Perfil guardado.</div>}
        {officeMsg.error && <div className="text-sm text-[hsl(var(--destructive))]">{officeMsg.error}</div>}
        <div><button className="btn-primary" disabled={savingOffice} data-testid="med-office-submit">{savingOffice ? "Guardando…" : "Guardar perfil profesional"}</button></div>
      </form>

      {/* Tarifario · nuevo modelo (base + acuerdo_total con cap $130.602) */}
      <form onSubmit={saveTariff} className="mt-6 panel p-6" data-testid="mediator-tariff-form">
        <div className="flex items-center gap-2 mb-3">
          <Coins size={18} className="text-amber-300"/>
          <h3 className="serif text-xl">Tarifario neto de mediación</h3>
        </div>
        <p className="text-xs text-muted-foreground max-w-2xl mb-4">
          Ingresa dos valores netos que recibirás íntegros: <strong>tu honorario por mediación frustrada</strong> y el <strong>monto adicional</strong> que cobras si se llega a acuerdo total. Colega Link cobra por separado <strong>{CLP(INITIAL_FEE)} fijo al iniciar</strong> y un <strong>{Math.round(AGREEMENT_RATE * 10000) / 100}% sólo sobre el monto adicional del acuerdo</strong>. Tope máximo total que paga la persona solicitante: {CLP(CAP)}.
        </p>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Precio por mediación frustrada (CLP) *">
            <input type="number" min={1} step={1} className="field" required inputMode="numeric"
              value={tariffForm.precio_mediador}
              onChange={(e)=>setTariffForm({...tariffForm, precio_mediador: parseInt(e.target.value || "0", 10)})}
              placeholder="Ej: 50000"
              data-testid="med-tariff-base"/>
          </Field>
          <Field label="Precio adicional con acuerdo total (CLP) *">
            <input type="number" min={0} step={1} className="field" required inputMode="numeric"
              value={tariffForm.precio_acuerdo_adicional}
              onChange={(e)=>setTariffForm({...tariffForm, precio_acuerdo_adicional: parseInt(e.target.value || "0", 10)})}
              placeholder="Ej: 30000"
              data-testid="med-tariff-agreement"/>
          </Field>
        </div>
        <p className="text-[11px] text-muted-foreground mt-2 flex items-start gap-1.5" data-testid="calc-max-hint">
          <Info size={11} className="text-muted-foreground mt-0.5 shrink-0"/>
          <span>El monto máximo por acuerdo es de <strong className="text-foreground">{CLP(maxAdicional)}</strong>, según el tope legal tras descontar tu honorario base y el cargo ColegaLink.</span>
        </p>

        <div className="mt-5 grid sm:grid-cols-2 gap-4">
          <div className="p-4 rounded-md bg-white/5 border border-white/10 text-sm space-y-1" data-testid="calc-frustrada">
            <div className="label-eyebrow text-muted-foreground">Escenario · mediación frustrada</div>
            <div className="flex justify-between mt-1"><span className="text-muted-foreground">Tu honorario</span><span className="font-medium">{CLP(base)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Cargo fijo Colega Link</span><span>{CLP(INITIAL_FEE)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="font-medium">Precio total al solicitante</span><span className="font-medium text-cyan-300" data-testid="calc-total-frustrada">{CLP(totalFrustrada)}</span></div>
            <div className="flex justify-between mt-2"><span className="text-emerald-300">Tú recibes</span><span className="text-emerald-300 font-medium">{CLP(base)}</span></div>
          </div>
          <div className={`p-4 rounded-md border text-sm space-y-1 ${overCap ? "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10" : "border-emerald-400/25 bg-emerald-400/5"}`} data-testid="calc-acuerdo">
            <div className="label-eyebrow text-emerald-300">Escenario · con acuerdo total</div>
            <div className="flex justify-between mt-1"><span className="text-muted-foreground">Precio total frustrada (ya cobrado)</span><span>{CLP(totalFrustrada)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Precio adicional acuerdo</span><span>{CLP(adicional)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Comisión Colega Link ({Math.round(AGREEMENT_RATE * 10000) / 100}% del adicional)</span><span>{CLP(comision)}</span></div>
            <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="font-medium">Total final al solicitante</span><span className={`font-medium ${overCap ? "text-[hsl(var(--destructive))]" : "text-emerald-300"}`} data-testid="calc-total-acuerdo">{CLP(totalAcuerdo)}</span></div>
            <div className="flex justify-between mt-2"><span className="text-emerald-300">Tú recibes total</span><span className="text-emerald-300 font-medium">{CLP(acuerdo)}</span></div>
            {overCap && <div className="text-[11px] text-[hsl(var(--destructive))] mt-1" data-testid="calc-over-cap">Excede el tope legal de {CLP(CAP)}. Ajusta el honorario base y/o el adicional por acuerdo.</div>}
          </div>
        </div>

        {tariffMsg.ok && <div className="mt-4 text-sm text-[hsl(var(--accent))]" data-testid="med-tariff-saved">Tarifario guardado.</div>}
        {tariffMsg.error && <div className="mt-4 text-sm text-[hsl(var(--destructive))]" data-testid="med-tariff-error">{tariffMsg.error}</div>}
        <div className="mt-4"><button className="btn-primary" disabled={savingTariff || overCap} data-testid="med-tariff-submit">{savingTariff ? "Guardando…" : "Guardar tarifario"}</button></div>
      </form>
    </div>
  );
}
